import type { ChildProfile, FeatureFlags, SessionPlan } from "@guiplay/contracts";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, post } from "./api";

interface Guardian { email: string; role: "owner" | "guardian" | "platform_admin" }

interface GuiPlayState {
  loading: boolean;
  guardian: Guardian | null;
  profiles: ChildProfile[];
  selectedChild: ChildProfile | null;
  flags: FeatureFlags;
  login(email: string, accessCode: string): Promise<void>;
  logout(): Promise<void>;
  refresh(): Promise<void>;
  createChild(input: { nickname: string; birthMonth: string; uiLocale: "pt-BR" | "en-US"; learningLocale: "pt-BR" | "en-US"; avatar: string; coreLearningConsent: true }): Promise<ChildProfile>;
  selectChild(child: ChildProfile): void;
  startSession(childId: string): Promise<SessionPlan>;
  updateLimits(childId: string, sessionLimitMinutes: number, dailyLimitMinutes: number): Promise<void>;
}

const safeFlags: FeatureFlags = {
  speech_enabled: false,
  ai_parent_insights_enabled: false,
  ai_content_generation_enabled: false,
  space_theme_enabled: false,
  public_signup_enabled: false,
  commercial_enabled: false,
  us_market_enabled: false,
  offline_sessions_enabled: false,
  full_cms_enabled: false
};

const Context = createContext<GuiPlayState | null>(null);

export function GuiPlayProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [guardian, setGuardian] = useState<Guardian | null>(null);
  const [profiles, setProfiles] = useState<ChildProfile[]>([]);
  const [flags, setFlags] = useState<FeatureFlags>(safeFlags);
  const [selectedId, setSelectedId] = useState<string | null>(() => sessionStorage.getItem("gp_child"));

  const refresh = useCallback(async () => {
    try {
      const result = await api<{ guardian: Guardian; profiles: ChildProfile[]; flags: FeatureFlags }>("/me/bootstrap");
      setGuardian(result.guardian);
      setProfiles(result.profiles);
      setFlags({ ...safeFlags, ...result.flags });
      if (!selectedId && result.profiles[0]) setSelectedId(result.profiles[0].id);
    } catch {
      setGuardian(null);
      setProfiles([]);
      setFlags(safeFlags);
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => { void refresh(); }, []);

  const value = useMemo<GuiPlayState>(() => ({
    loading,
    guardian,
    profiles,
    selectedChild: profiles.find((profile) => profile.id === selectedId) ?? profiles[0] ?? null,
    flags,
    async login(email, accessCode) {
      await post("/auth/pilot", { email, accessCode });
      await refresh();
    },
    async logout() {
      await post("/auth/logout", {});
      sessionStorage.removeItem("gp_child");
      setGuardian(null);
      setProfiles([]);
      setSelectedId(null);
    },
    refresh,
    async createChild(input) {
      const result = await post<{ profile: ChildProfile }>("/children", input);
      setProfiles((current) => [...current, result.profile]);
      setSelectedId(result.profile.id);
      sessionStorage.setItem("gp_child", result.profile.id);
      return result.profile;
    },
    selectChild(child) {
      setSelectedId(child.id);
      sessionStorage.setItem("gp_child", child.id);
    },
    async startSession(childId) {
      const result = await post<{ session: SessionPlan }>("/sessions/start", { childId });
      return result.session;
    },
    async updateLimits(childId, sessionLimitMinutes, dailyLimitMinutes) {
      await api(`/children/${childId}/preferences`, { method: "PATCH", body: JSON.stringify({ sessionLimitMinutes, dailyLimitMinutes }) });
      await refresh();
    }
  }), [flags, guardian, loading, profiles, refresh, selectedId]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useGuiPlay(): GuiPlayState {
  const value = useContext(Context);
  if (!value) throw new Error("useGuiPlay must be used inside GuiPlayProvider");
  return value;
}
